import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Button, Flex, Select, Text } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CurrencyType,
	GetInvestorInvestmentDetailsRes,
	SecurityType,
	VestingTermSimplifiedRes,
} from '@dealstack/shared/lib/generated';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { GenericDrawer } from '@dealstack/frontend/shared/components/GenericDrawer/GenericDrawer';
import { ContainerWithHeader } from '@dealstack/frontend/shared/components/ContainerWithHeader/ContainerWithHeader';
import { SummaryItem } from '@dealstack/frontend/shared/components/SummaryItem/SummaryItem';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	DATE_FORMATS,
	formatStringDateToUI,
	parseBEDateIgnoreTimezone,
	parseUiDateDdMmYyyy,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { AmountFormatted } from '@dealstack/frontend/shared/components/AmountFormatted/AmountFormatted.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { DateInput } from '@mantine/dates';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { VestingSecuritySummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/VestingSecuritySummary.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { extractDecimalPrecisionFromUnitDenominator } from '@dealstack/shared/lib/format/number.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	vestingTerms: VestingTermSimplifiedRes[];
	loading?: boolean;
	currency?: CurrencyType;
	selectedInvestment: GetInvestorInvestmentDetailsRes | undefined;
	onVestingTermApply: (vestingTermId: string, investmentId: string) => void;
	onVestingTermRemove: (investmentId: string) => void;
	onVestingDateChange: (investmentId: string, startDate: Date | null) => void;
	getInvestmentReference: (...props) => string;
	getSecurityTypeTranslation: (...props) => string;
}

export const VestingTermsDrawer = ({
	opened,
	onClose,
	vestingTerms,
	loading,
	currency,
	selectedInvestment,
	onVestingTermApply,
	onVestingTermRemove,
	onVestingDateChange,
	getInvestmentReference,
	getSecurityTypeTranslation,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const [dateInputValue, setDateInputValue] = useState<Date | null>(null);

	useEffect(() => {
		if (selectedInvestment?.vestingStartDate) {
			setDateInputValue(parseBEDateIgnoreTimezone(selectedInvestment.vestingStartDate));
		} else {
			setDateInputValue(null);
		}
	}, [selectedInvestment?.vestingStartDate, selectedInvestment?.investmentId]);

	const formattedCompletedDate = formatStringDateToUI(
		selectedInvestment?.completedDate,
		DATE_FORMATS.CALENDAR_DATE
	);
	const firstInvestment = Object.values(selectedInvestment?.investmentUnits || {})[0];

	const summaryItems = [
		{
			title: tMipManage('investors.expanded_row.investment_reference'),
			value:
				getInvestmentReference(
					selectedInvestment?.investmentWorkflowComputedType,
					!!selectedInvestment?.completedDate,
					selectedInvestment?.completedDate ?? selectedInvestment?.createdDate
				) || LONG_DASH,
		},
		{
			title: tMipManage('investors.expanded_row.total_investment'),
			children: (
				<AmountFormatted
					value={firstInvestment?.value}
					currency={currency}
					textProps={{
						fz: 'lg',
					}}
					decimalScale={extractDecimalPrecisionFromUnitDenominator(
						firstInvestment?.securityUnitDenominator
					)}
				/>
			),
		},
		{
			title: tMipManage('investors.expanded_row.security_type'),
			children: (
				<SecurityTypeSummary
					investmentUnits={selectedInvestment?.investmentUnits}
					getSecurityTypeTranslation={getSecurityTypeTranslation}
				/>
			),
		},
		{
			title: tMipManage('vesting_terms_drawer.investment_securities_label'),
			children: (
				<VestingSecuritySummary securities={selectedInvestment?.investmentSecurities} />
			),
		},
		{
			title: tMipManage('investors.expanded_row.date_completed'),
			value: formattedCompletedDate,
		},
	];
	const vestingStartDateWarnings = useMemo(
		() => [
			...(selectedInvestment?.vestingTermId
				? []
				: [tMipManage('vesting_terms_drawer.start_date_missing_vesting_term')]),
			...(selectedInvestment?.vestingStartDate || !selectedInvestment?.vestingTermId
				? []
				: [
						tMipManage('vesting_terms_drawer.vesting_default_start_date_info', {
							defaultDate: formattedCompletedDate,
						}),
					]),
		],
		[
			formattedCompletedDate,
			selectedInvestment?.vestingStartDate,
			selectedInvestment?.vestingTermId,
			tMipManage,
		]
	);

	const handleVestingStartDateChange = (date: Date | null) => {
		setDateInputValue(date);

		if (selectedInvestment?.investmentId) {
			onVestingDateChange(selectedInvestment?.investmentId, date);
		} else {
			showToastV2({
				title: tMipManage('vesting_terms_drawer.missing_vesting_sa_id'),
				state: 'error',
			});
		}
	};

	return (
		<GenericDrawer
			opened={opened}
			onClose={onClose}
			transitionProps={drawerTransitionProps}
			position="right"
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VESTING_DRAWER}
			title={<Text size="B4Bold">{tMipManage('vesting_terms_drawer.title')}</Text>}
			body={
				<Flex direction="column" h="100%">
					{loading ? <InlineLoader /> : null}

					<Flex direction="column" justify="space-between" h="100%">
						<Flex direction="column" gap="1rem">
							<InfoBox
								iconVariant="information"
								rippleColor="blue"
								description={tMipManage(
									'vesting_terms_drawer.investment_securities_info'
								)}
							/>

							<ContainerWithHeader
								title={tMipManage('vesting_terms_drawer.investment_summary')}
							>
								<Flex direction="column" gap="1rem" mb="1rem">
									{summaryItems.map(({ title, value, children }, index) => (
										<SummaryItem
											item={TEST_ELEMENT_IDS.ADMIN_MIP_CARDS}
											key={`summary-${index}`}
											title={title}
											margin="0"
										>
											{value && (
												<Text fz="lg" fw={400}>
													{value}
												</Text>
											)}
											{children}
										</SummaryItem>
									))}
									<Flex align="flex-end" gap="0.5rem">
										<Select
											rightSection={<Icon variant="caret-down" size="lg" />}
											rightSectionProps={{ style: { pointerEvents: 'none' } }}
											data={
												vestingTerms?.map((vestingTerm) => ({
													label: vestingTerm.name,
													value: vestingTerm.id,
												})) ?? []
											}
											w="100%"
											size="md"
											label={
												<Text fz="md" c="dark.0" fw={400} mb="0.25rem">
													{tMipManage(
														'investors.expanded_row.vesting_term'
													)}
												</Text>
											}
											value={selectedInvestment?.vestingTermId}
											onChange={(vestingTermId) =>
												selectedInvestment &&
												onVestingTermApply(
													vestingTermId ?? '',
													selectedInvestment.investmentId
												)
											}
											placeholder={tMipManage('investors.select')}
										/>
										<Button
											variant="subtle"
											color="red"
											disabled={loading}
											h="40px"
											style={{ flexShrink: 0 }}
											onClick={() =>
												selectedInvestment &&
												onVestingTermRemove(selectedInvestment.investmentId)
											}
										>
											{tMipManage('investors.remove_short')}
										</Button>
									</Flex>
									<Flex direction="column" gap="0.25rem">
										<DateInput
											label={
												<Text fz="md" c="dark.0" fw={400} mb="0.25rem">
													{tMipManage(
														'vesting_terms_drawer.vesting_start_date_label'
													)}
												</Text>
											}
											data-testid={
												TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VESTING_START_DATE_INPUT
											}
											dateParser={parseUiDateDdMmYyyy}
											value={dateInputValue}
											size="md"
											disabled={loading || !selectedInvestment?.vestingTermId}
											clearable
											placeholder={tMipManage('investors.select')}
											valueFormat="DD/MM/YYYY"
											onChange={handleVestingStartDateChange}
										/>
										<div>
											{vestingStartDateWarnings.map((warning, index) => (
												<Text
													key={`vestingStartDateWarning${index}`}
													fz="xs"
												>
													{warning}
												</Text>
											))}
										</div>
									</Flex>
								</Flex>
							</ContainerWithHeader>
						</Flex>
					</Flex>
				</Flex>
			}
		/>
	);
};

const SecurityTypeSummary = ({
	investmentUnits,
	getSecurityTypeTranslation,
}: {
	investmentUnits?: GetInvestorInvestmentDetailsRes['investmentUnits'];
	getSecurityTypeTranslation: Props['getSecurityTypeTranslation'];
}) => {
	return (
		<Flex direction="column" gap="0.5rem">
			{Object.entries(investmentUnits || {}).map(([securityTypeKey]) => (
				<Text fz="lg" fw={400} key={securityTypeKey}>
					{getSecurityTypeTranslation(securityTypeKey as SecurityType)}
				</Text>
			))}
		</Flex>
	);
};
